import React, { useState, useEffect, useRef, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { tableOptions } from '../components/TableDefaults';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { makeStyles } from '@mui/styles';
import {
  Modal,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
} from '@mui/material';
import PolylineIcon from '@mui/icons-material/Polyline';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PlaceIcon from '@mui/icons-material/Place';
import { GoogleMap, DrawingManager, Polygon } from '@react-google-maps/api';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 800,
    maxWidth: '95vw',
    height: 600,
    backgroundColor: '#FFFFFF',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    padding: theme.spacing(3),
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));


export default function Zones() {
  const { t,i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editZone
  } = api;

  const columns = [
    { title: t('zone_name'), field: 'zone_name',
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PlaceIcon sx={{ color: '#6366F1', fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
            {rowData.zone_name || '-'}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('active'), field: 'active', type:'boolean', initialEditValue: true,
      render: rowData => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 10, color: rowData.active ? '#10B981' : '#9CA3AF' }} />
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            {rowData.active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
          </Typography>
        </Box>
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('zone_dispatch_mode') || 'Zone dispatch mode', field: 'dispatch_mode',
      lookup: { '': t('default') || 'Default', online_longest: t('mode_online_longest') || 'Online longest' },
      initialEditValue: '',
      render: rowData => (
        <Chip
          label={rowData.dispatch_mode === 'online_longest' ? (t('mode_online_longest') || 'Online longest') : (t('default') || 'Default')}
          size="small"
          sx={{ bgcolor: '#F3F4F6', color: '#374151', fontWeight: 500, fontSize: 12 }}
        />
      ),
      cellStyle: { padding: '14px 16px' },
    },
    { title: t('zone_path'), field: 'zone_path', editable: 'never',
      render: rowData => rowData.zone_path ? (
        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
          {rowData.zone_path.length} {t('points') || 'points'}
        </Typography>
      ) : <Typography sx={{ fontSize: 12, color: '#9CA3AF' }}>-</Typography>,
      cellStyle: { padding: '14px 16px' },
    }
  ];
  const settings = useSelector(state => state.settingsdata.settings);
  const usersdata = useSelector(state => state.usersdata);
  const [data, setData] = useState([]);
  const zonelistdata = useSelector(state => state.zonelistdata);
  const dispatch = useDispatch();
  const [open,setOpen] = useState(false);
  const [rowIndex,setRowIndex] = useState();
  const classes = useStyles();
  const rootRef = useRef(null);
  const [polygon, setPolygon] = useState();
  const [mylocation, setMylocation] = useState(null);
  const [zoneDrivers, setZoneDrivers] = useState({});
  const mapRef = useRef(null);
   
  useEffect(() => {
    if (zonelistdata.zones) {
      setData(zonelistdata.zones);
    }else{
      setData([]);
    }
  }, [zonelistdata.zones]);

  const handleClose = () => {
    setOpen(false);
  }

  const handlePolygonComplete = polygon => {
    const arr = [];
    let zone = data[rowIndex];
    polygon.getPaths().getArray().map(path => path.getArray().forEach((position) => arr.push([ position.lat(), position.lng()])));
    if (arr.length < 3) {
      alert(t('zone_polygon_error') || 'Please draw a polygon with at least 3 points.');
      return;
    }
    // persist polygon path + derived centroid/bounds for convenience
    zone['zone_path'] = arr;
    try {
      let minLat=arr[0][0], maxLat=arr[0][0], minLng=arr[0][1], maxLng=arr[0][1];
      for (let i=1;i<arr.length;i++){
        const [lat,lng] = arr[i];
        if(lat<minLat) minLat=lat; if(lat>maxLat) maxLat=lat;
        if(lng<minLng) minLng=lng; if(lng>maxLng) maxLng=lng;
      }
      const centroid = [(minLat+maxLat)/2, (minLng+maxLng)/2];
      zone['centroid'] = centroid;
      zone['bounds'] = { ne: [maxLat, maxLng], sw: [minLat, minLng] };
    } catch(e){}
    dispatch(editZone(zone, "Edit"));
    setOpen(false);
  };

  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [activeAnchor, setActiveAnchor] = useState(null);

  // Filtered data
  const filteredData = useMemo(() => {
    let result = data;
    if (filterActive !== 'all') {
      result = result.filter(d => filterActive === 'active' ? d.active : !d.active);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(d =>
        (d.zone_name && d.zone_name.toLowerCase().includes(query))
      );
    }
    return result;
  }, [data, filterActive, searchQuery]);

  const clearFilters = () => {
    setFilterActive('all');
    setSearchQuery('');
  };

  // client-side point-in-polygon (robust to [lat,lng] or [lng,lat] and object forms)
  const pip = (lat, lng, poly) => {
    if (!poly || poly.length < 3) return false;

    const toLngLatA = (p) => {
      if (Array.isArray(p)) return [Number(p[1]), Number(p[0])]; // assume [lat,lng]
      if (p && typeof p === 'object') return [Number(p.lng), Number(p.lat)];
      return [NaN, NaN];
    };
    const toLngLatB = (p) => {
      if (Array.isArray(p)) return [Number(p[0]), Number(p[1])]; // assume [lng,lat]
      if (p && typeof p === 'object') return [Number(p.lng), Number(p.lat)];
      return [NaN, NaN];
    };

    const test = (mapFn) => {
      let inside = false;
      for (let i=0,j=poly.length-1; i<poly.length; j=i++){
        const [x1,y1] = mapFn(poly[i]); // x=lng, y=lat
        const [x2,y2] = mapFn(poly[j]);
        if (Number.isNaN(x1) || Number.isNaN(y1) || Number.isNaN(x2) || Number.isNaN(y2)) continue;
        const intersect = ((y1>lat)!==(y2>lat)) && (lng < (x2 - x1) * (lat - y1) / ((y2 - y1) || 1e-9) + x1);
        if (intersect) inside = !inside;
      }
      return inside;
    };

    return test(toLngLatA) || test(toLngLatB);
  };

  // build real-time view of drivers grouped by active zones
  useEffect(() => {
    if(!usersdata.users || !data) return;
    const drivers = usersdata.users.filter(u=>u.usertype==='driver' && u.driverActiveStatus && u.location);
    const zones = data.filter(z=>z.active && Array.isArray(z.zone_path) && z.zone_path.length>=3);
    const groups = {};
    zones.forEach(z=>{ groups[z.zone_name] = []; });
    drivers.forEach(d=>{
      const { lat, lng } = d.location;
      for (let z of zones){
        if (pip(lat, lng, z.zone_path)){
          groups[z.zone_name].push(d);
          break;
        }
      }
    });
    setZoneDrivers(groups);
  }, [usersdata.users, data]);

  return (
    zonelistdata.loading ? <CircularLoading /> :
    <div ref={rootRef}>
      {/* Uber-Style Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          p: 2,
          mb: 2,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          alignItems: 'center',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <TextField
          size="small"
          placeholder={t('search') || 'Search zones...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              borderRadius: 50,
              bgcolor: '#F7F7F7',
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#EFEFEF' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#6B7280', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ width: 1, height: 24, bgcolor: '#E5E7EB', mx: 1 }} />
        <Chip
          label={t('all') || 'All'}
          onClick={clearFilters}
          sx={{
            bgcolor: filterActive === 'all' ? '#111827' : '#F7F7F7',
            color: filterActive === 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterActive === 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {filterActive !== 'all' && (
                <FiberManualRecordIcon sx={{ fontSize: 8, color: filterActive === 'active' ? '#10B981' : '#9CA3AF' }} />
              )}
              {filterActive === 'all' ? (t('status') || 'Status') : filterActive === 'active' ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}
              <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
            </Box>
          }
          onClick={(e) => setActiveAnchor(e.currentTarget)}
          sx={{
            bgcolor: filterActive !== 'all' ? '#111827' : '#F7F7F7',
            color: filterActive !== 'all' ? '#FFFFFF' : '#374151',
            fontWeight: 500,
            fontSize: 13,
            height: 36,
            borderRadius: 50,
            px: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: filterActive !== 'all' ? '#000000' : '#EFEFEF' },
          }}
        />
        <Menu
          anchorEl={activeAnchor}
          open={Boolean(activeAnchor)}
          onClose={() => setActiveAnchor(null)}
          PaperProps={{ sx: { borderRadius: 2, mt: 1, minWidth: 150, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
        >
          <MenuItem onClick={() => { setFilterActive('all'); setActiveAnchor(null); }}>
            <Typography sx={{ fontSize: 14 }}>{t('all') || 'All'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterActive('active'); setActiveAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#10B981', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('active') || 'Active'}</Typography>
          </MenuItem>
          <MenuItem onClick={() => { setFilterActive('inactive'); setActiveAnchor(null); }}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: '#9CA3AF', mr: 1 }} />
            <Typography sx={{ fontSize: 14 }}>{t('inactive') || 'Inactive'}</Typography>
          </MenuItem>
        </Menu>
        <Typography sx={{ ml: 'auto', fontSize: 13, color: '#6B7280' }}>
          {filteredData.length} {t('zones') || 'zones'}
        </Typography>
      </Box>

      <Box sx={{ mb:2 }}>
        <Card sx={{ borderRadius:2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb:1 }}>{t('drivers_by_zone') || 'Drivers by zone (online)'}</Typography>
            <Grid container spacing={2}>
              {Object.keys(zoneDrivers).length===0 ? (
                <Grid item xs={12}><Typography variant="body2" sx={{ opacity:0.8 }}>{t('no_zones') || 'No active zones'}</Typography></Grid>
              ) : (
                Object.keys(zoneDrivers).map((zname)=> (
                  <Grid key={zname} item xs={12} md={6} lg={4}>
                    <Box sx={{ p:1.5, border:'1px solid #e7e7e7', borderRadius:2 }}>
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight:600 }}>{zname}</Typography>
                        <Chip size="small" label={zoneDrivers[zname].length} />
                      </Box>
                      {/* car type distribution */}
                      {(() => {
                        const counts = {};
                        zoneDrivers[zname].forEach(d => {
                          const k = d.carType || '—';
                          counts[k] = (counts[k] || 0) + 1;
                        });
                        const entries = Object.entries(counts);
                        return entries.length ? (
                          <Box sx={{ mt:0.75, display:'flex', gap:0.5, flexWrap:'wrap' }}>
                            {entries.map(([k,v]) => (
                              <Chip key={k} size="small" variant="outlined" label={`${k}: ${v}`} />
                            ))}
                          </Box>
                        ) : null;
                      })()}
                      {zoneDrivers[zname].slice(0,8).map(d=> (
                        <Typography key={d.id} variant="caption" display="block">{(d.firstName||'') + ' ' + (d.lastName||'')}</Typography>
                      ))}
                      {zoneDrivers[zname].length>8 ? (
                        <Typography variant="caption" sx={{ opacity:0.7 }}>+{zoneDrivers[zname].length-8} {t('more') || 'more'}</Typography>
                      ): null}
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
      <MaterialTable
        title={null}
        icons={TableIcons}
        columns={columns}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: 12,
          boxShadow: 'none',
          border: '1px solid #E5E7EB',
          overflow: 'hidden',
        }}
        data={filteredData}
        onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
        options={{
          ...tableOptions,
          search: false,
          toolbar: false,
          showTitle: false,
          exportButton: true,
          pageSize: 10,
          rowStyle: rowData => ({
            backgroundColor: (selectedRow === rowData.tableData.id) ? '#F3F4F6' : '#fff'
          }),
        }}
        localization={{body:{
          addTooltip: (t('add')),
          deleteTooltip: (t('delete')),
          editTooltip: (t('edit')),
          emptyDataSourceMessage: (
            (t('blank_message'))
        ),
        editRow: { 
          deleteText: (t('delete_message')),
          cancelTooltip: (t('cancel')),
          saveTooltip: (t('save')) 
            }, 
          },
          toolbar: {
            searchPlaceholder: (t('search')),
            exportTitle: (t('export')),
          },
          pagination: {
            labelDisplayedRows: ('{from}-{to} '+ (t('of'))+ ' {count}'),
            firstTooltip: (t('first_page_tooltip')),
            previousTooltip: (t('previous_page_tooltip')),
            nextTooltip: (t('next_page_tooltip')),
            lastTooltip: (t('last_page_tooltip'))
          },
        }}
        editable={settings.AllowCriticalEditsAdmin ? {
            onRowAdd: newData =>
            new Promise((resolve, reject)=> {
              setTimeout(() => {
                if(!(newData && newData.zone_name)){
                  alert(t('no_details_error'));
                  reject();
                }else{
                  dispatch(editZone(newData, "Add"));
                  resolve();
                }
              }, 600);
            }),
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject)=> {
              setTimeout(() => {
                if(!(newData && newData.zone_name)){
                  alert(t('no_details_error'));
                  reject();
                }else {
                  dispatch(editZone(newData, "Update"));
                  resolve()
                }
              }, 600);
            }),
          onRowDelete: oldData =>
            new Promise(resolve => {
              setTimeout(() => {
                resolve();
                dispatch(editZone(oldData, "Delete"));
              }, 600);
            }),
        } : null}
        actions={[
          rowData => ({
            icon: () => <div style={{display: 'flex',alignItems: 'center',flexWrap: 'wrap'}}>
                <PolylineIcon />
                <Typography variant="subtitle2" style={{padding: 5}}>{t('edit_zone_path')}</Typography>
            </div>,
            onClick: (event, rowData) => {
              setRowIndex(rowData.tableData.id);
              setOpen(true);
              if(rowData.zone_path && rowData.zone_path.length>0){
                let arr = [];
                for(let i=0;i<rowData.zone_path.length;i++){
                  const path = rowData.zone_path[i];
                  arr.push(new window.google.maps.LatLng(path[0], path[1]));
                }
                setPolygon(arr);
                let obj = JSON.stringify(arr[0]);
                let obj2 = JSON.parse(obj);
                setMylocation({
                  lat: obj2.lat,
                  lng: obj2.lng
                })
              } else{
                setMylocation(null);
                setPolygon();
              }
            }
          })
        ]}
      />
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        onClose={handleClose}
        open={open}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <div className={classes.paper}>
          <GoogleMap
              zoom={polygon? 8 : 2}
              center={{ lat: mylocation? mylocation.lat : 30.00, lng: mylocation? mylocation.lng : 31.00 }}
              mapContainerStyle={{ height: '100%' }}
              onLoad={(map)=>{ mapRef.current = map; }}
          >
              <DrawingManager
                  drawingMode={'polygon'}
                  options={{drawingControl: false}}
                  onPolygonComplete={handlePolygonComplete}
              />
              {polygon && polygon.length > 0 && (
                <Polygon paths={polygon} />
              )}
          </GoogleMap>
          {polygon && polygon.length>0 ? (
            <div style={{ position:'absolute', top: 12, right: 16 }}>
              <Button size="small" variant="contained" onClick={()=>{
                try{
                  const bounds = new window.google.maps.LatLngBounds();
                  polygon.forEach(p=>bounds.extend(p));
                  mapRef.current && mapRef.current.fitBounds(bounds);
                }catch(e){}
              }}>{t('fit_to_polygon') || 'Fit to polygon'}</Button>
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
